import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { QueryState } from './QueryState';
import { useAccountDay } from '../accountDay';
import { readableInstant } from '../dateLabels';
import {
  listCalendarAccounts,
  connectCalendarAccount,
  disconnectCalendarAccount,
  listCalendarRows,
  setCalendarSync,
  syncAllCalendars,
  listCalendarConflicts,
  resolveCalendarConflict,
  ApiError,
  type CalendarAccount,
} from '../api';

function AccountRow({ account }: { account: CalendarAccount }) {
  const queryClient = useQueryClient();
  const calendars = useQuery({ queryKey: ['calendars'], queryFn: listCalendarRows });
  /* Sync times on the account's clock, not this laptop's — see `dateLabels.ts`. */
  const { timezone } = useAccountDay();

  const toggle = useMutation({
    mutationFn: (args: { id: string; on: boolean }) => setCalendarSync(args.id, args.on),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['calendars'] }),
  });

  const disconnect = useMutation({
    mutationFn: () => disconnectCalendarAccount(account.id),
    onSuccess: () => queryClient.invalidateQueries(),
  });

  const mine = (calendars.data ?? []).filter((c) => c.calendarAccountId === account.id);
  const needsReauth = account.syncStatus === 'reauth_required';

  return (
    <li className="card">
      <div className="template-head">
        <strong>{account.email}</strong>
        <span className="task-meta">
          <span className={`pill ${needsReauth ? 'bad' : 'ok'}`}>{account.syncStatus}</span>
          {account.lastSyncAt && (
            <span className="pill">synced {readableInstant(account.lastSyncAt, timezone)}</span>
          )}
        </span>
      </div>

      {needsReauth && (
        <p className="banner">
          Google access was revoked. Reconnect this account to resume syncing.
          {account.lastError ? ` (${account.lastError})` : ''}
        </p>
      )}

      <ul className="calendar-list">
        {mine.map((calendar) => (
          <li key={calendar.id}>
            <label className="check">
              <input
                type="checkbox"
                checked={calendar.syncEnabled}
                onChange={(e) => toggle.mutate({ id: calendar.id, on: e.target.checked })}
              />
              {calendar.name}
            </label>
            {calendar.isPrimary && <span className="pill">primary</span>}
            {!calendar.isWritable && (
              <span className="pill" title="Google says we may not write here">
                read-only
              </span>
            )}
            {calendar.lastSyncAt && (
              <span className="muted size">
                {readableInstant(calendar.lastSyncAt, timezone, 'clock')}
              </span>
            )}
          </li>
        ))}
      </ul>

      <button
        className="link danger"
        onClick={() => {
          if (confirm(`Disconnect ${account.email}? Its stored tokens are deleted.`))
            disconnect.mutate();
        }}
      >
        disconnect
      </button>
    </li>
  );
}

function ConflictInbox() {
  const queryClient = useQueryClient();
  const conflicts = useQuery({ queryKey: ['conflicts'], queryFn: listCalendarConflicts });
  const { timezone } = useAccountDay();

  const resolve = useMutation({
    mutationFn: (args: { id: string; keep: 'mine' | 'theirs' }) =>
      resolveCalendarConflict(args.id, args.keep),
    onSuccess: () => queryClient.invalidateQueries(),
  });

  if (!conflicts.data?.length) return null;

  return (
    <div className="card">
      <h2>Conflicts</h2>
      <p className="muted">
        These changed here and in Google since the last sync. Both versions are kept — pick one;
        nothing is merged and nothing wins automatically.
      </p>
      <ul>
        {conflicts.data.map((c) => (
          <li key={c.id} className="conflict-row">
            <span>
              <strong>{c.title}</strong>{' '}
              <span className="muted">{readableInstant(c.startsAt, timezone)}</span>
            </span>
            <span>
              <button onClick={() => resolve.mutate({ id: c.id, keep: 'mine' })}>Keep mine</button>
              <button onClick={() => resolve.mutate({ id: c.id, keep: 'theirs' })}>
                Keep theirs
              </button>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function CalendarSettings() {
  const queryClient = useQueryClient();
  const [code, setCode] = useState('');
  const [message, setMessage] = useState<string | null>(null);

  const accounts = useQuery({ queryKey: ['calendarAccounts'], queryFn: listCalendarAccounts });

  const connect = useMutation({
    mutationFn: () => connectCalendarAccount(code.trim()),
    onSuccess: () => {
      setCode('');
      setMessage(null);
      void queryClient.invalidateQueries();
    },
    onError: (e) => setMessage(e instanceof ApiError ? e.message : 'Could not connect.'),
  });

  const sync = useMutation({
    mutationFn: syncAllCalendars,
    onSuccess: (results) => {
      setMessage(
        results.length === 0
          ? 'Nothing is switched on to sync yet.'
          : results.map((r) => r.summary).join('; '),
      );
      void queryClient.invalidateQueries();
    },
  });

  return (
    <section>
      <h2>Google Calendar</h2>
      <p className="muted">
        Connect as many accounts as you like — personal and work are two grants against the same
        app. Choose per calendar whether it syncs.
      </p>

      <ConflictInbox />

      <form
        className="capture"
        onSubmit={(e) => {
          e.preventDefault();
          if (code.trim()) connect.mutate();
        }}
      >
        <input
          value={code}
          placeholder="Authorisation code from Google"
          onChange={(e) => setCode(e.target.value)}
        />
        <button type="submit" disabled={!code.trim() || connect.isPending}>
          Connect account
        </button>
        <button type="button" onClick={() => sync.mutate()} disabled={sync.isPending}>
          Sync now
        </button>
      </form>

      {message && <p className="banner ok-banner">{message}</p>}

      <QueryState query={accounts} />
      {accounts.data?.length === 0 && <p className="muted">No accounts connected.</p>}

      <ul className="account-list">
        {accounts.data?.map((account) => (
          <AccountRow key={account.id} account={account} />
        ))}
      </ul>
    </section>
  );
}
